import { z } from "zod";

export const SortEnum = z.enum(["asc", "desc"]);
export type SortEnum = z.infer<typeof SortEnum>;

/*
There are limitations to the "sort nulls last/first" functionality.
- it *has* to be an optional field. If you specify a required field,
  instead of just ignoring it Prisma will throw an error
- it won't work if the field is required on an optional relationship
*/

type OrderBySortNulls = { sort: SortEnum; nulls: "last" };

type OrderByNestedValue = SortEnum | OrderBySortNulls | OrderByObject | OrderByObject[];

/** Object-shaped orderBy fragment (nested relation sorts from dot-path field names). */
export type OrderByObject = { [key: string]: OrderByNestedValue };

export type SortToOrderByFn<TOrderBy = OrderByObject> = (sortDir: SortEnum) => TOrderBy;

/**
 * Builds a Prisma-style orderBy from a saved-filter sort field (optional dot path).
 * Map overrides supply entity-specific shapes; unknown fields use nulls-last or scalar sort.
 */
/** Prefer a known list-column sort field; otherwise use the page default so Prisma never sees a leaked foreign column. */
export function resolveAllowedSortField(sortField: string, allowedFields: readonly string[], fallbackField: string): string {
  return allowedFields.includes(sortField) ? sortField : fallbackField;
}

export function sortToOrderBy<TOrderBy = OrderByObject | OrderByObject[]>(
  sortField: string,
  sortDir: SortEnum,
  map: Map<string, SortToOrderByFn<TOrderBy>>,
  nullsLastFields: string[] = [],
): TOrderBy | undefined {
  if (sortField === "") {
    return undefined;
  }

  const pieces = sortField.split(".");
  const fieldName = pieces.pop();
  if (fieldName === undefined || fieldName === "") {
    return undefined;
  }

  const fromMap = map.get(fieldName)?.(sortDir);
  // Map callbacks return TOrderBy; nested wrapping uses object shapes Prisma accepts at runtime.
  let rv: OrderByObject | OrderByObject[] =
    (fromMap as OrderByObject | OrderByObject[] | undefined) ??
    (nullsLastFields.includes(sortField) ? { [fieldName]: { sort: sortDir, nulls: "last" } } : { [fieldName]: sortDir });

  while (pieces.length > 0) {
    const level = pieces.pop();
    if (level === undefined || level === "") {
      break;
    }
    rv = { [level]: rv };
  }

  // Map hits are TOrderBy; dot-path / nulls-last fallbacks match Prisma orderBy shapes but TS cannot relate them to TOrderBy.
  return rv as TOrderBy;
}

/** When list search uses a text query, non-archived rows sort before archived (matches customers + suppliers tables). */
export function orderByWithArchivedFirstForQuery<TOrderBy>(
  hasTextQuery: boolean,
  baseOrderBy: TOrderBy | TOrderBy[] | undefined,
  archivedFirst: TOrderBy,
): TOrderBy | TOrderBy[] | undefined {
  if (!hasTextQuery) {
    return baseOrderBy;
  }
  if (!baseOrderBy) {
    return [archivedFirst];
  }
  const tail = Array.isArray(baseOrderBy) ? baseOrderBy : [baseOrderBy];
  return [archivedFirst, ...tail];
}
